import { createSelector } from "@reduxjs/toolkit";
import { getDeeplyNestedValue } from "@multiconsult-group/multiplacer-core";
import { openapi, type PostFunctionsDrofusOccurrencesDetailsApiResponse as ILiveOccurrence, type PostMultiplacerDrofusInterfaceOccurrenceApiResponse as ISavedOccurrence } from "../openapi";
import type { IRootState } from "./app/store";
import { selectProjectNo, selectCompCodePath } from "./globalSelectors";
import { selectClickedMcBimItemId, selectClickedObjectRoomNumber } from "./ViewerRedux/viewerSlice";
import { selectHttpGetMcbimItem } from "./components/McBimItem/singleMcbimItemSelectors";
import { buildDrofusMatchView, needsRoomFallback, normalizeArticleCode, type MatchContext } from "./DrofusMatch/drofusMatchLogic";

export const selectDrofusMatchContext = createSelector(
  [selectProjectNo, selectClickedMcBimItemId, selectHttpGetMcbimItem, selectCompCodePath,
    selectClickedObjectRoomNumber],
  (projectNo, itemId, item, codePath, room): MatchContext => ({
    projectNo, itemId, ready: !!item,
    ifcGuid: item?.globalId?.trim() || null,
    roomFunctionNo: room?.trim() || null,
    articleCode: item && codePath ? normalizeArticleCode(getDeeplyNestedValue(item, codePath)) : null,
  })
);
export const selectDrofusMatchContextKey = createSelector([selectDrofusMatchContext], context => JSON.stringify(context));
export const selectDrofusMatchGuidArg = createSelector([selectDrofusMatchContext], c =>
  c.ready && c.projectNo && c.itemId && c.ifcGuid ? { body: { projectNo: c.projectNo, ifcGuid: c.ifcGuid } } : null
);
const selectGuidQuerySelector = createSelector([selectDrofusMatchGuidArg], arg =>
  arg ? openapi.endpoints.postMultiplacerDrofusInterfaceOccurrenceByIfcGuid.select(arg) : null
);
const selectGuidQuery = createSelector([selectGuidQuerySelector, (s: IRootState) => s], (query, state) => query?.(state));
export const selectDrofusMatchRoomArg = createSelector([selectDrofusMatchContext, selectGuidQuery], (c, guid) =>
  needsRoomFallback(c, guid) && c.roomFunctionNo && c.articleCode
    ? { body: { projectNo: c.projectNo, roomFunctionNo: c.roomFunctionNo } } : null
);
const selectRoomQuerySelector = createSelector([selectDrofusMatchRoomArg], arg =>
  arg ? openapi.endpoints.postMultiplacerDrofusInterfaceOccurrencesInRoom.select(arg) : null
);
const selectRoomQuery = createSelector([selectRoomQuerySelector, (s: IRootState) => s], (query, state) => query?.(state));
export const selectDrofusMatchView = createSelector(
  [selectDrofusMatchContext, selectGuidQuery, selectRoomQuery], buildDrofusMatchView
);
export type DrofusMatchSelection = { occurrenceId: number; runId: string } | null;
export const selectDrofusMatchDetailsSnapshot = createSelector(
  [selectDrofusMatchView, selectGuidQuery, selectRoomQuery,
    (_state: IRootState, selected: DrofusMatchSelection) => selected],
  (view, guid, room, selected) => {
    if (!selected || !view.rows.some(row => row.occurrenceId === selected.occurrenceId && row.runId === selected.runId)) return null;
    const matches = (item: NonNullable<typeof guid>["data"]) =>
      item?.occurrenceId === selected.occurrenceId && item.runId === selected.runId;
    if (matches(guid?.data)) return guid?.data ?? null;
    return room?.data?.find(matches) ?? null;
  }
);

export const selectDrofusOccurrenceDetailsArg = createSelector(
  [selectProjectNo, (_state: IRootState, occurrenceId: number | null) => occurrenceId],
  (projectNo, occurrenceId) =>
    projectNo && occurrenceId ? { body: { projectNo, occurrenceId } } : null
);

interface IDetailsQuery<T> {
  currentData?: T;
  isFetching: boolean;
  isUninitialized: boolean;
  isError: boolean;
  error?: unknown;
}
export function needsLiveDrofusOccurrenceDetails(saved: IDetailsQuery<ISavedOccurrence>): boolean {
  return !saved.isFetching && saved.isError &&
    typeof saved.error === "object" && saved.error !== null &&
    "status" in saved.error && saved.error.status === 404;
}

interface IOccurrenceDetailsInput {
  live: IDetailsQuery<ILiveOccurrence>;
  saved: IDetailsQuery<ISavedOccurrence>;
  snapshot?: ISavedOccurrence | null;
}
const selectLiveOccurrenceDetails = (input: IOccurrenceDetailsInput) => input.live;
const selectSavedOccurrenceDetails = (input: IOccurrenceDetailsInput) => input.saved;
const selectOccurrenceDetailsSnapshot = (input: IOccurrenceDetailsInput) => input.snapshot;
export const selectDrofusOccurrenceDetailsView = createSelector(
  [selectLiveOccurrenceDetails, selectSavedOccurrenceDetails, selectOccurrenceDetailsSnapshot],
  (live, saved, snapshot) => {
    if (snapshot) return { row: snapshot, source: "saved" as const, isFetching: false, isError: false, error: undefined };
    const useLive = needsLiveDrofusOccurrenceDetails(saved);
    const query = useLive ? live : saved;
    return {
      row: query.currentData,
      source: useLive ? "live" as const : "saved" as const,
      isFetching: query.isFetching || query.isUninitialized,
      isError: query.isError,
      error: query.error,
    };
  }
);
